import { takeLatest, put, call, select } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { PROJECTION } from 'constants/ApiConstants';
import { PATH } from 'constants/RoutesConstants';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import { scopeToPage } from 'modules/app/sagas/utils';
import * as actions from 'modules/auditModels/actions/dashboard';
import * as types from 'modules/auditModels/actionTypes';
import { AUDIT_MODEL_STATUS } from 'modules/auditModels/constants';
import { selectDealer } from 'modules/dealers/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { getSelectedDealer, getSelectedDealerId } from 'selectors/dealerSelectors';

/** @param {import('../types').FetchAuditModelsMyAction} */
function* fetchMyAuditModels({ payload, meta }) {
  const { request } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put(actions.fetchAuditModelsMySuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchAuditModelsMyFailure(error, meta));
  }
}

/** @param {import('../types').FetchAuditModelsAvailableAction} action */
function* fetchAvailableAuditModels({ payload }) {
  const { request } = payload;
  try {
    const data = yield* fetchPage(request);
    yield put(actions.fetchAuditModelsAvailableSuccess(data));
  } catch (error) {
    yield put(actions.fetchAuditModelsAvailableFailure(error));
  }
}

/** @param {import('../types').PatchAuditModelStatusAction} action */
function* patchAuditModelStatus({ payload }) {
  const { request } = payload;
  let type = 'success';
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.patchAuditModelStatusSuccess(data));
  } catch (error) {
    yield put(actions.patchAuditModelStatusFailure(error));
    type = 'error';
  }

  const action = payload.request.data.status === AUDIT_MODEL_STATUS.OPEN ? 'publish' : 'unpublish';
  yield put(
    createNotification({
      type,
      message: `audito.page.models.notification.${action}.${type}`,
    })
  );
}

/** @param {import('../types').DeleteAuditModelAction} action */
function* deleteAuditModel({ payload }) {
  const { request, auditModelId } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteAuditModelSuccess(auditModelId));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.page.models.notification.delete.success',
          defaultMessage: 'Audit template deleted',
        },
      })
    );
  } catch (error) {
    yield put(actions.deleteAuditModelFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.models.notification.delete.error',
          defaultMessage: "Can't delete the audit template",
        },
      })
    );
  }
}

/** @param {import('../types').DeactivateAuditModelAction} action */
function* deactivateAuditModel({ payload }) {
  const { request } = payload;
  try {
    const { data: auditModel } = yield call(restClient.request, request);
    yield put(actions.deactivateAuditModelSuccess(auditModel));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.page.models.notification.deactivate.success',
          defaultMessage: 'Audit template deactivated',
        },
      })
    );
  } catch (error) {
    yield put(actions.deactivateAuditModelFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.models.notification.deactivate.error',
          defaultMessage: "Can't delete the audit template",
        },
      })
    );
  }
}

/** @param {import('../types').ActivateAuditModelAction} action */
function* activateAuditModel({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: auditModel } = yield call(restClient.request, request);
    yield put(actions.activateAuditModelSuccess(auditModel, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'audito.page.models.notification.activate.success',
          defaultMessage: 'Audit template activated',
        },
      })
    );
  } catch (error) {
    yield put(actions.activateAuditModelFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'audito.page.models.notification.activate.error',
          defaultMessage: "Can't activate the audit template",
        },
      })
    );
  }
}

function* refetchDealerWithConfigProjection() {
  const dealerId = yield select(getSelectedDealerId);
  const dealer = yield select(getSelectedDealer);

  if (dealerId && !dealer?.assignments) {
    yield put(selectDealer(dealerId, { projection: PROJECTION.CONFIG }));
  }
}

export default function* auditModelsDashboardSagas() {
  yield takeLatest(types.DELETE_AUDIT_MODEL, deleteAuditModel);
  yield takeLatest(types.PATCH_AUDIT_MODEL_STATUS, patchAuditModelStatus);
  yield takeLatest(types.FETCH_AUDIT_MODELS_MY, fetchMyAuditModels);
  yield takeLatest(types.FETCH_AUDIT_MODELS_AVAILABLE, fetchAvailableAuditModels);
  yield takeLatest(types.DEACTIVATE_AUDIT_MODEL, deactivateAuditModel);
  yield takeLatest(types.ACTIVATE_AUDIT_MODEL, activateAuditModel);

  yield scopeToPage([PATH.AUDIT_MODELS], refetchDealerWithConfigProjection);
}
